import {
  ComponentRef,
  Directive,
  ElementRef,
  EnvironmentInjector,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
  createComponent,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { TextTooltipBubbleComponent } from './TextTooltipBubble.component';

// Infobulle texte au-dessus de son hote ; l'overlay vit dans `document.body` (rognage des lignes).
@Directive({
  selector: '[appTextTooltip]',
  exportAs: 'appTextTooltip',
  standalone: true,
})
export class TextTooltipDirective implements OnDestroy {
  /** Texte deja traduit ; vide : pas de bulle. */
  @Input('appTextTooltip') text: string | null = null;

  /** Delai avant apparition, en millisecondes. */
  @Input() tooltipDelay = 150;

  /** Cote prefere ; bascule de l'autre cote si la place manque. */
  @Input() tooltipPlacement: 'top' | 'bottom' = 'top';

  /** Decalage en px vers l'hote (positif = bulle plus proche). */
  @Input() tooltipOffset = 0;

  /** Surcharge optionnelle du theme sombre (couleurs CSS quelconques). */
  @Input() tooltipBackground: string | null = null;
  @Input() tooltipTextColor: string | null = null;
  @Input() tooltipBorder: string | null = null;

  /** Hauteur de la pointe ; l'ecart avec l'hote vaut pointe + air. */
  private static readonly POINTE = 6;
  private static readonly AIR = 6;
  private static readonly ECART = TextTooltipDirective.POINTE + TextTooltipDirective.AIR;

  /** Marge minimale avec les bords de la fenetre. */
  private static readonly BORD = 8;

  /** La pointe ne mord jamais sur le coin arrondi de la bulle. */
  private static readonly MARGE_POINTE = 10;

  private bubble: ComponentRef<TextTooltipBubbleComponent> | null = null;
  private bubbleEl: HTMLElement | null = null;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private removeGlobalListeners: (() => void) | null = null;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly envInjector: EnvironmentInjector,
    @Inject(DOCUMENT) private readonly doc: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) { }

  @HostListener('mouseenter') onEnter(): void { this.scheduleShow(); }
  @HostListener('mouseleave') onLeave(): void { this.hide(); }
  @HostListener('focusin') onFocus(): void { this.scheduleShow(); }
  @HostListener('focusout') onBlur(): void { this.hide(); }

  private scheduleShow(): void {
    if (!this.text || this.bubble) return;
    this.showTimer ??= setTimeout(() => { this.showTimer = null; this.show(); }, this.tooltipDelay);
  }

  private show(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.text || this.bubble) return;

    const ref = createComponent(TextTooltipBubbleComponent, {
      environmentInjector: this.envInjector,
    });
    const el = ref.location.nativeElement as HTMLElement;
    this.bubble = ref;
    this.bubbleEl = el;

    try {
      ref.setInput('text', this.text);
      if (this.tooltipBackground) this.renderer.setStyle(el, '--tt-fond', this.tooltipBackground);
      if (this.tooltipTextColor) this.renderer.setStyle(el, '--tt-texte', this.tooltipTextColor);
      if (this.tooltipBorder) this.renderer.setStyle(el, '--tt-bordure', this.tooltipBorder);
      // La bulle est hors de l'arbre applicatif : son rendu est pilote ici.
      this.renderer.appendChild(this.doc.body, el);
      ref.changeDetectorRef.detectChanges();
      this.place();
    } catch {
      // On ne laisse jamais une bulle orpheline si le calcul echoue.
      this.destroyBubble();
      return;
    }

    this.addGlobalListeners();
  }

  /** Mesure puis pose la bulle. Idempotent : sert aussi a repositionner. */
  private place(): void {
    const ref = this.bubble;
    const el = this.bubbleEl;
    if (!ref || !el) return;

    const hote = this.el.nativeElement.getBoundingClientRect();
    const boite = el.getBoundingClientRect();
    const vue = this.doc.defaultView;
    if (!vue) return;

    const largeurVue = vue.innerWidth;
    const hauteurVue = vue.innerHeight;
    const centreHote = hote.left + hote.width / 2;

    // Cote : on part du cote demande, on bascule seulement si l'autre tient vraiment.
    let placement = this.tooltipPlacement;
    let haut = placement === 'top'
      ? hote.top - boite.height - TextTooltipDirective.ECART + this.tooltipOffset
      : hote.bottom + TextTooltipDirective.ECART - this.tooltipOffset;

    if (placement === 'top' && haut < TextTooltipDirective.BORD) {
      const dessous = hote.bottom + TextTooltipDirective.ECART - this.tooltipOffset;
      if (dessous + boite.height <= hauteurVue - TextTooltipDirective.BORD) {
        placement = 'bottom';
        haut = dessous;
      }
    } else if (placement === 'bottom' && haut + boite.height > hauteurVue - TextTooltipDirective.BORD) {
      const dessus = hote.top - boite.height - TextTooltipDirective.ECART + this.tooltipOffset;
      if (dessus >= TextTooltipDirective.BORD) {
        placement = 'top';
        haut = dessus;
      }
    }

    // Le max() est interieur a dessein : borner sans lui inverserait les deux bornes.
    const hautMax = Math.max(TextTooltipDirective.BORD, hauteurVue - boite.height - TextTooltipDirective.BORD);
    haut = Math.min(Math.max(haut, TextTooltipDirective.BORD), hautMax);

    const gaucheMax = Math.max(TextTooltipDirective.BORD, largeurVue - boite.width - TextTooltipDirective.BORD);
    const gauche = Math.min(Math.max(centreHote - boite.width / 2, TextTooltipDirective.BORD), gaucheMax);

    // La pointe vise le centre de l'hote, ramene dans le repere de la bulle.
    const pointe = Math.min(
      Math.max(centreHote - gauche, TextTooltipDirective.MARGE_POINTE),
      Math.max(TextTooltipDirective.MARGE_POINTE, boite.width - TextTooltipDirective.MARGE_POINTE),
    );

    this.renderer.setStyle(el, 'top', `${haut}px`);
    this.renderer.setStyle(el, 'left', `${gauche}px`);
    ref.setInput('placement', placement);
    ref.setInput('arrowOffset', pointe);
    ref.changeDetectorRef.detectChanges();
  }

  private addGlobalListeners(): void {
    // En phase de capture : sans cela, le defilement des conteneurs internes ne remonte pas.
    const offScroll = this.renderer.listen('window', 'scroll', () => this.hide(), { capture: true, passive: true });
    const offResize = this.renderer.listen('window', 'resize', () => this.hide());
    this.removeGlobalListeners = () => { offScroll(); offResize(); };
  }

  /** Ferme la bulle. Idempotent. */
  hide(): void {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    this.destroyBubble();
  }

  private destroyBubble(): void {
    const ref = this.bubble;
    const el = this.bubbleEl;
    if (!ref || !el) return;

    this.bubble = null;
    this.bubbleEl = null;
    this.removeGlobalListeners?.();
    this.removeGlobalListeners = null;

    // `destroy()` ne retire PAS l'hote du DOM : d'ou ce removeChild.
    this.renderer.removeChild(this.doc.body, el);
    ref.destroy();
  }

  ngOnDestroy(): void {
    this.hide();
  }
}
